import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { useState } from "react";
import { Plus, Check } from "lucide-react";

export function Modal({ open, onClose, title, children, wide, xl, logo }) {
  if (!open) return null;
  const width = xl ? "max-w-[1700px]" : wide ? "max-w-3xl" : "max-w-lg";
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 no-print">
      <div
        data-modal
        className={`${xl ? "mt-6" : "mt-10"} w-full ${width} bg-[#FFFFFF] border border-[#E3E8EE] shadow-xl`}
      >
        {logo ? (
          <div className="relative border-b border-[#E3E8EE] px-8 pt-7 pb-5">
            <button onClick={onClose} data-testid="modal-close" className="absolute right-6 top-6 text-[#5B6672] hover:text-[#16202B]">
              <X size={20} />
            </button>
            <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="mx-auto w-[130px] mb-3" />
            <h2 className="font-display font-bold text-xl tracking-tight text-[#16202B] text-center">{title}</h2>
          </div>
        ) : (
          <div className="flex items-center justify-between border-b border-[#E3E8EE] px-6 py-4">
            <h2 className="font-display font-bold text-xl tracking-tight text-[#16202B]">{title}</h2>
            <button onClick={onClose} data-testid="modal-close" className="text-[#5B6672] hover:text-[#16202B]">
              <X size={20} />
            </button>
          </div>
        )}
        <div className={logo ? "p-8" : "p-6"}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const base =
    "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-widest transition-all duration-150 disabled:opacity-60 hover:-translate-y-px";
  const variants = {
    primary: "bg-[#465B7F] hover:bg-[#374A66] text-white",
    outline: "border border-[#1C1A17] text-[#1C1A17] hover:bg-[#1C1A17] hover:text-white",
    ghost: "text-[#5C5A55] hover:text-[#1C1A17] hover:bg-[#E9EEF3]",
    danger: "border border-[#ED1F26] text-[#ED1F26] hover:bg-[#ED1F26] hover:text-white",
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Input({ label, testid, className = "", ...props }) {
  return (
    <label className="block">
      {label && <span className="overline block mb-1.5">{label}</span>}
      <input
        data-testid={testid}
        className={`w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F] ${className}`}
        {...props}
      />
    </label>
  );
}

export function Select({ label, testid, children, ...props }) {
  return (
    <label className="block">
      {label && <span className="overline block mb-1.5">{label}</span>}
      <select
        data-testid={testid}
        className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]"
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function EmptyState({ title, subtitle, action }) {  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <img
        src="https://images.unsplash.com/photo-1518655048521-f130df041f66"
        alt=""
        className="w-40 h-40 object-cover mb-6 grayscale"
        style={{ mixBlendMode: "multiply", opacity: 0.7 }}
      />
      <div className="font-display font-bold text-xl text-[#1C1A17]">{title}</div>
      <p className="font-mono-data text-sm text-[#5C5A55] mt-2 mb-6 max-w-sm">{subtitle}</p>
      {action}
    </div>
  );
}


export function ListSkeleton({ rows = 8, testid = "list-skeleton" }) {
  return (
    <div className="p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-4 w-4 rounded-full border-2 border-[#465B7F] border-t-transparent animate-spin" />
        <span className="font-mono-data text-sm text-[#5B6672]">Cargando datos…</span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 py-2 border-b border-[#EEF2F6]">
          <div className="h-4 w-4 bg-[#E3E8EE] rounded animate-pulse" />
          <div className="h-4 flex-1 bg-[#E3E8EE] rounded animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
          <div className="h-4 w-24 bg-[#EDF1F5] rounded animate-pulse" />
          <div className="h-4 w-16 bg-[#EDF1F5] rounded animate-pulse" />
          <div className="h-4 w-20 bg-[#E3E8EE] rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}


export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = "Eliminar", confirmVariant = "danger", testid }) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <p className="font-mono-data text-sm text-[#5C5A55]">{message}</p>
      <div className="flex justify-end gap-3 mt-6">
        <Btn variant="ghost" type="button" onClick={onClose}>Cancelar</Btn>
        <Btn variant={confirmVariant} type="button" onClick={onConfirm} data-testid={testid}>{confirmLabel}</Btn>
      </div>
    </Modal>
  );
}

export function CreatableSelect({ label, value, onChange, options = [], onCreate, placeholder = "Selecciona…", addLabel = "➕ Agregar nueva categoría…", newPlaceholder = "Nueva categoría…", testid }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const confirm = async () => {
    const v = draft.trim();
    if (!v) return;
    if (onCreate && !options.some((o) => o.toLowerCase() === v.toLowerCase())) {
      await onCreate(v);
    }
    onChange(v);
    setDraft("");
    setAdding(false);
  };

  return (
    <label className="block">
      {label && <span className="overline block mb-1.5">{label}</span>}
      {adding ? (
        <div className="flex gap-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } }}
            placeholder={newPlaceholder}
            data-testid={`${testid}-new-input`}
            className="flex-1 bg-[#FFFFFF] border border-[#465B7F] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
          />
          <button type="button" onClick={confirm} data-testid={`${testid}-new-confirm`}
            className="px-3 border border-[#465B7F] text-[#465B7F] hover:bg-[#465B7F] hover:text-white transition-colors"><Check size={16} /></button>
          <button type="button" onClick={() => { setAdding(false); setDraft(""); }}
            className="px-3 border border-[#E3E8EE] text-[#5B6672] hover:text-[#16202B] transition-colors"><X size={16} /></button>
        </div>
      ) : (
        <select
          value={value || ""}
          data-testid={testid}
          onChange={(e) => { e.target.value === "__new__" ? setAdding(true) : onChange(e.target.value); }}
          className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value="__new__">{addLabel}</option>
        </select>
      )}
    </label>
  );
}
